import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "Why is a minimum of 300 Karma required?",
    a: "We partner with high-quality brands that value organic, high-reach placement. Accounts with at least 300 Karma have established histories in college, tech, or hobby subreddits, which ensures posts are well-received and avoid automatic moderation filters.",
  },
  {
    q: "How and when do I get paid?",
    a: "Once approved, you receive prompt links to post in matching subreddits. After posting, you paste the URL of your post into our task board. Once our system verifies it (typically within 15 minutes), ₹100 is credited instantly to your registered UPI ID.",
  },
  {
    q: "What kind of content will I be posting?",
    a: "You'll be posting helpful recommendations, sharing resources, or answering relevant questions naturally in subreddits. For example, sharing a study tool in a college community, or a career platform in a job board. We provide the guidelines, and you write the post organically.",
  },
  {
    q: "Do you need my Reddit password?",
    a: "No! We never ask for your password, private keys, or account access tokens. We only require your public Reddit profile username to fetch public karma statistics via standard API queries. Your account remains completely secure.",
  },
  {
    q: "Can I register multiple Reddit accounts?",
    a: "Yes! If you manage multiple Reddit accounts and each satisfies the minimum 300 Karma requirement, you can register them separately to receive more prompt tasks.",
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faqs" className="py-16 px-4 max-w-4xl mx-auto relative overflow-hidden scroll-mt-20">
      {/* Header */}
      <div className="text-center mb-14">
        <h2 className="text-4xl font-black text-[#3F51B5] tracking-tight mb-3 flex items-center justify-center gap-2">
          <HelpCircle className="w-8 h-8 text-[#FFC107] shrink-0" />
          Frequently Asked Questions
        </h2>
        <p className="text-slate-600 text-md font-semibold">
          Everything you need to know about the App20 Reddit Creator Program.
        </p>
      </div>

      {/* Accordions */}
      <div className="space-y-4">
        {FAQS.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="bg-white/90 border-2 border-slate-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#3F51B5] shadow-sm"
            >
              <button
                onClick={() => toggleFAQ(idx)}
                className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none"
              >
                <span className="font-extrabold text-[#3F51B5] text-md tracking-wide pr-4">
                  {faq.q}
                </span>
                <span className="w-7 h-7 rounded-full bg-[#FFC107] flex items-center justify-center border border-amber-300 shrink-0 shadow-sm">
                  {isOpen ? (
                    <Minus className="w-4 h-4 text-black" />
                  ) : (
                    <Plus className="w-4 h-4 text-black" />
                  )}
                </span>
              </button>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <div className="px-6 pb-6 text-slate-700 text-sm leading-relaxed border-t border-slate-100 pt-4 font-medium">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}
